<template>
  <component
    :is="as"
    class="panel-list"
    :class="[
      'panel-list--' + tone,
      'panel-list--' + layout,
      'panel-list--mark-' + mark,
      'panel-list--cols-' + cols,
      {
        'panel-list--stacked': stacked,
        'panel-list--bare': bare,
        'panel-list--still': still,
        'panel-list--trailing': trailing
      }
    ]"
  >
    <p v-if="title" class="panel-list-title">{{ title }}</p>

    <!-- The paired ledger, and the one layout whose list is not a `ul`.

         A label over its value is what a definition list is, so this renders a
         real `dl` rather than a `ul` of two-part rows: the markup says what the
         content is, and a screen reader announces each pair as a term and its
         description without being told to. The `div` between them is the
         grouping element a `dl` allows, and it is what lets a pair be a grid row
         and carry the reveal. -->
    <dl v-if="layout === 'pairs'" class="panel-list-items">
      <div
        v-for="(item, index) in items"
        :key="item.label || index"
        class="panel-list-item"
        data-aos="fade-up"
      >
        <!-- The mark is optional here, and it is the item that decides rather
             than the layout: a pair whose subject has a mark to give shows it,
             and one whose subject is only ever a word reads as a plain line
             without reserving the space for a tile that is not coming. It is the
             same tile the rows layout fronts a choice with, at the same size and
             variant, because a mark on a panel is one object however the row
             behind it is arranged. -->
        <icon-tile
          v-if="mark === 'icon' && item.icon"
          class="panel-list-mark"
          :icon="item.icon"
          :variant="markVariant"
          :size="markSize"
        />

        <dt class="panel-list-label">{{ item.label }}</dt>
        <dd class="panel-list-value">
          <!-- The value is text by default and a node when it has to be. A fact
               whose value leaves the page is a link, and a link is not a string:
               the caller supplies the whole element through this slot rather
               than the component guessing that a value with an `href` should be
               one. Everything else takes the plain text and the component never
               has to know which of the two it is rendering. -->
          <slot name="value" :item="item" :index="index">{{ item.value }}</slot>
        </dd>
      </div>
    </dl>

    <ul v-else class="panel-list-items">
      <li
        v-for="(item, index) in items"
        :key="item.title || index"
        class="panel-list-item"
        data-aos="fade-up"
      >
        <span v-if="mark === 'number'" class="panel-list-num" aria-hidden="true">
          {{ String(index + 1).padStart(2, '0') }}
        </span>
        <icon-tile
          v-else-if="item.icon || icon"
          class="panel-list-mark"
          :icon="item.icon || icon"
          :variant="markVariant"
          :size="markSize"
          :stacked="stacked"
        />

        <div class="panel-list-copy">
          <h3 class="panel-list-head">{{ item.title }}</h3>
          <p v-if="item.body" class="panel-list-body">{{ item.body }}</p>
        </div>

        <div v-if="$slots.extra" class="panel-list-extra">
          <slot name="extra" :item="item" :index="index" />
        </div>
      </li>
    </ul>

    <slot />
  </component>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* One panel anatomy for every "mark + title + body" list on the site.

   This block was written seven times with the same internals: the same
   $panel-row-pad rhythm, the same first/last trims, the same hairline beam
   along the top edge, the same 2.4rem tile, and the same hover response (the
   mark lifts and glows, the title takes the accent). Nine copies of the
   translucent surface fill and its hairline sat behind those seven, and every
   copy had drifted somewhere: the row gap, the panel padding, the beam inset,
   the title size and the lift distance all differed by a step or two depending
   on which page you were on.

   The axes, which cover every one of them:
     layout   rows    one surface holding a ruled list, hairline above each row
              pairs   the same ruled panel, its first column a label and its
                      second the value the label names, rendered as a `dl`
              cards   one surface per item, in a grid
     tone     light   a light surface with the $primary accent
              dark    $on-dark-fill with white titles and the $dark-accent
     mark     icon    the 2.4rem tile that fronts a row of choices
              number  a two-digit numeral, for a list that is a sequence
              stacked  cards only: the mark sits above the copy instead of beside it
     bare     a surrounding slab already owns the surface, so the list only
              divides it: no fill, no beam, no lift, no padding
     still    the list is a document, not a set of choices, so nothing answers
              the pointer
     trailing the row ends with a block from the extra slot, e.g. the file
              formats the brand manifest shows beside each piece

   The tone only chooses the shadow under the lift. A blue glow reads on white
   and a black one on a dark ground, which is why the same panel used to need
   two hand-written hover rules per page.

   The light tone paints an opaque ground, so it carries anywhere. The dark tone
   does not: it is a film, and a film needs something behind it. That is what
   `bare` is for on a dark panel, and the ground behind it is InkSlab.

   `pairs` is the one layout that is not a list of choices, so two of the axes do
   not apply to it: `mark` and `trailing` say what the rows layout's first and
   last columns hold, and a pair's columns are its label and its value. Its items
   are `{label, value}` rather than `{title, body}`, because the two name
   different things: a title is a heading and a label is a name for the value
   beside it, and a pair puts the weight on the value. */

/* The resting box, minus any response. Kept apart from panel-response so a
   card can have the surface without the pointer behaviour (still). */
@mixin panel-box($bg, $line) {
  background: $bg;
  border: 1px solid $line;
  border-radius: $r-lg;
  position: relative;
}

/* Hairline beam along the top edge, inset past the corner radius so it never
   pokes out of the rounded corners. The gradient is a custom property so the
   response below can reveal the same beam on every tone. */
@mixin panel-beam($accent) {
  --panel-beam: linear-gradient(90deg, transparent, #{$accent}, transparent);

  &::after {
    background: var(--panel-beam);
    border-radius: $r-xs;
    content: '';
    height: 2px;
    left: 1.5rem;
    position: absolute;
    right: 1.5rem;
    top: 0;
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform $dur-slow $ease;
  }
}

@mixin panel-response($line-hover, $lift) {
  transition: border-color $dur $ease, box-shadow $dur $ease, transform $dur $ease;

  &:hover,
  &:focus-within {
    border-color: $line-hover;
    box-shadow: $lift;
    transform: translateY(-3px);

    &::after {
      transform: scaleX(1);
    }
  }
}

@mixin panel-lift {
  .panel-list-mark {
    box-shadow: $shadow-primary;
    transform: translateY(-3px) scale(1.07);
  }
}

/* A panel inside a slab that already owns the surface (the order sheet on the
   brand presence page, the promo brief's ink panel, the portfolio case brief)
   keeps its anatomy and drops the box: no fill, no hairline beam, no radius,
   because the slab is what divides one row from the next.

   Declared once, because it is the same cancel on both layouts. The rows list
   and the pair ledger are the same panel with a different first column, so the
   fill, the border and the beam belong to the panel rather than to the layout.
   This was written under the rows block alone, which left a bare pair painting
   its film and its border inside the very slab it was supposed to belong to: a
   box floating in a box, and the opposite of what `bare` says.

   The first row keeps its rule and its full padding either way, because the
   slab supplies the edges: the list is what divides the slab. */
@mixin panel-bare {
  background: none;
  border: 0;
  border-radius: 0;

  &::after {
    content: none;
  }

  .panel-list-item {
    &:first-child {
      border-top: 1px solid var(--panel-rule);
      padding-top: $panel-row-pad;
    }

    &:last-child {
      padding-bottom: $panel-row-pad;
    }
  }
}

/* The ruled panel's surface, one per tone.

   The rows layout and the pairs layout are the same panel with a different first
   column, so they take the same fill, the same hairline border and the same beam.
   Declaring the pair once here is what stops the second layout from being a
   repaint of the first, which is how the seven hand-written panels drifted before
   this component existed. */
@mixin panel-surface-light {
  @include panel-box($ground-accent, $tint-primary);
  @include panel-beam($primary);
}

/* A panel already standing on ink. The 5% white film is what raises it off that
   ground, so this surface is only ever right where something dark is already
   behind it: a `section.dark`, a promo band, an InkSlab. On a light band the
   same film is a nearly-transparent white wash with white type on it, which is a
   panel that cannot be read rather than a panel that did not show. A dark panel
   that has to stand on a light page belongs inside an InkSlab, which paints the
   ground the film needs. */
@mixin panel-surface-dark {
  @include panel-box($on-dark-fill, $on-dark-line);
  @include panel-beam($dark-accent);
}

.panel-list {
  --panel-mark: 2.4rem;
}

.panel-list--mark-sm {
  --panel-mark: 2.1rem;
}

.panel-list--mark-number {
  --panel-mark: 2.25rem;
}

.panel-list-items {
  list-style: none;
  margin: 0;
  padding: 0;
}

/* The mark beside the copy, which is how a ruled row reads.

   The mark tops out with the copy instead of centring against the row. A
   2.4rem tile is taller than two lines of body text, so centring it dropped it
   below its own heading: every row showed an icon floating beside the middle of
   its own paragraph, and nothing in the row lined up. Top-aligned, the tile's
   top edge and the heading's first line start together, which is what makes the
   rows scan as a list. */
.panel-list-item {
  align-items: start;
  display: grid;
  gap: 0.95rem;
  grid-template-columns: var(--panel-mark) minmax(0, 1fr);
}

/* A numeral is text, not a tile, so it does not read as aligned just because
   its box tops out with the heading. The nudge is what lands the two-digit
   figure on the copy's first baseline, where the eye expects a numbered list to
   start. */
.panel-list-num {
  color: $text-muted;
  font-family: $headings-font-family;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  padding-top: 0.15rem;
}

/* The panel's label. The type is the shared $label-* step; the colour comes from
   the ground, declared below. It used to sit inside the rows block, so a cards
   panel on a dark ground kept the light-ground colour: the layout was deciding
   something that belongs to the ground. It is the same $label-on-* answer
   SectionTitle's kicker takes, because it is the same role. */
.panel-list-title {
  font-family: $headings-font-family;
  font-size: $label-size;
  font-weight: $label-weight;
  letter-spacing: $label-tracking;
  /* More air under the kicker than the 0.4rem the first version used, so it
     does not read as part of the first row. */
  margin-bottom: 0.7rem;
  text-transform: uppercase;
}

.panel-list--light .panel-list-title {
  color: $label-on-light;
}

.panel-list--dark .panel-list-title {
  color: $label-on-dark;
}

/* The row or card title. It is an h3 in both layouts: the page hero owns the
   h1, every section head is an h2, and the title of a row or a card inside a
   section is an h3. Half of these were <strong> and half were <h3> before, so
   the same kind of title was in the outline on one page and out of it on the
   next. */
.panel-list-head {
  color: $text-heading;
  display: block;
  font-family: $headings-font-family;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.35;
  margin: 0 0 0.15rem;
  transition: color $dur $ease;
}.panel-list-body {
  color: $text-label;
  font-size: 0.9rem;
  line-height: 1.55;
  margin-bottom: 0;
}

/* The two halves of a pair, which is the rows layout's two columns under
   different names.

   The label is the same $label-* step as the panel's own kicker above the list
   and takes the same per-ground colour below, because it is the same job one
   level down: a small uppercase name for what follows it. It is deliberately not
   a second treatment, so a list that has both reads as one thing saying its name
   twice at two scales rather than two labels that happen to look alike.

   The value takes the row title's spec, because it is the row's heading in
   everything but markup. The label is only there to say what the value is, so
   the value is what the eye lands on, and giving it the body step would invert
   the two. */
.panel-list-label {
  font-family: $headings-font-family;
  font-size: $label-size;
  font-weight: $label-weight;
  letter-spacing: $label-tracking;
  text-transform: uppercase;
}

.panel-list-value {
  color: $text-heading;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0;
}

/* A value that is a link rather than a statement takes the value's own type step
   instead of the button's. The link variant is the site's inline affordance and
   carries its own step from the "read more" it was built for, which is smaller
   than the value that states the same kind of thing. Only the size is borrowed:
   the ink, the hover and the outward arrow are still AppButton's. Declared here
   rather than on the page because it is the value column's type, and a second
   caller with a link in a pair would otherwise write it again. */
.panel-list-value .app-btn {
  font-size: inherit;
}

.panel-list--light .panel-list-label {
  color: $label-on-light;
}

.panel-list--dark .panel-list-label {
  color: $label-on-dark;
}

.panel-list--dark .panel-list-value {
  color: $on-dark-strong;
}

/* Rows: one surface, hairline above each row ------------------------------ */
.panel-list--rows {
  --panel-rule: #{$tint-primary};

  &.panel-list--dark {
    --panel-rule: #{$on-dark-line};
  }

  &:not(.panel-list--bare) {
    padding: 1.6rem 1.5rem;

    @media (min-width: 992px) {
      padding: 1.9rem 1.8rem;
    }
  }

  &.panel-list--light {
    @include panel-surface-light;
  }

  &.panel-list--dark {
    @include panel-surface-dark;

    .panel-list-num {
      color: $dark-accent;
    }

    .panel-list-head {
      color: $on-dark-strong;
    }

    .panel-list-body {
      color: $on-dark-body;
    }
  }

  &:not(.panel-list--still) {
    /* No row fill. The hovered row used to paint a translucent pill behind
       itself, which turned one ruled list into a set of floating cards and
       painted over the hairlines that give the panel its structure. The mark
       lift and the title colour are the whole response now. */
    .panel-list-item {
      &:hover,
      &:focus-within {
        @include panel-lift;
      }
    }

    /* The panel's own response: it lifts off the page, the beam runs, every
       title takes the accent. A bare list sits inside a slab that owns the
       surface, so it must stay put, and `bare` is excluded here at the source
       rather than cancelled in the --bare block below. A cancel is four
       class-level selectors and this rule is five, because `:not(...)` carries
       its argument's weight, so the cancel lost and every bare list still
       lifted. Excluding it here is the only version that cannot lose that
       argument a second time. */
    &.panel-list--light:not(.panel-list--bare) {
      @include panel-response($tint-primary-strong, $shadow-lift);

      &:hover,
      &:focus-within {
        .panel-list-head {
          color: $primary;
        }
      }
    }

    &.panel-list--dark:not(.panel-list--bare) {
      @include panel-response($on-dark-line-strong, $shadow-dark);

      &:hover,
      &:focus-within {
        .panel-list-head {
          color: $dark-accent;
        }
      }
    }
  }

  .panel-list-item {
    border-top: 1px solid var(--panel-rule);
    padding: $panel-row-pad 0;

    &:first-child {
      border-top: 0;
      padding-top: $panel-row-trim;
    }

    /* The trim at each end only holds when something else closes the list: on a
       boxed panel the panel's own padding supplies the outer space, and on a
       bare list the `--bare` block below puts this padding back. Getting that
       wrong is what makes a row's bottom edge land flush on its own last line of
       text, which is very visible once the page draws a closing rule there. */
    &:last-child {
      padding-bottom: 0;
    }
  }

  /* A ruled list can run as a grid, so six short rows read as two or three
     columns instead of one long ladder. The column gap is wider than the row
     gap because the rules are what separates rows; columns have no rule. */
  &.panel-list--cols-2 .panel-list-items {
    @media (min-width: 992px) {
      column-gap: 3.5rem;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  &.panel-list--cols-3 .panel-list-items {
    @media (min-width: 992px) {
      column-gap: 3.5rem;
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  /* Inside a slab that already owns the surface (the order sheet on the brand
     presence page, the promo brief's ink panel, the case brief's slab), the list
     keeps the anatomy and drops the box. The cancel itself is `panel-bare`,
     declared once for both layouts; what stays here is the reason, and the row
     feedback that is this layout's own.

     The pointer response is not cancelled in the mixin: it is excluded by the
     `:not(.panel-list--bare)` guards above, for the reason given there. Padding
     is excluded the same way, at the top of this block. Both are the exclusion
     pattern rather than a cancel, so neither depends on winning a specificity
     contest with a rule that is already heavier.

     The row still answers its own pointer: the mark lifts when the row under it
     is hovered. That is row-level, not panel-level, and it is the only feedback
     a bare list gives, which is what separates `bare` from `still`. */
  &.panel-list--bare {
    @include panel-bare;
  }

  /* A row that ends with a trailing block. The copy column takes what is left
     rather than a fixed share, so the trailing block is only as wide as the
     artwork inside it. On a narrow screen the block drops under the copy
     instead of squeezing it. */
  &.panel-list--trailing .panel-list-item {
    grid-template-columns: var(--panel-mark) minmax(0, 1fr);

    .panel-list-extra {
      grid-column: 2;
    }

    @media (min-width: 768px) {
      grid-template-columns: var(--panel-mark) minmax(0, 1fr) max-content;

      /* The artwork centres against the row; the mark and the copy do not. The
         row used to centre everything, and because this selector is more
         specific than the numeral rule it quietly won there, dropping the
         manifest's two-digit mark off the first baseline it is documented to
         sit on. Centring belongs on the artwork, which is the one part whose
         height does not line up with the text. */
      .panel-list-extra {
        align-self: center;
        grid-column: auto;
      }
    }
  }
}

/* Pairs: a ruled ledger of label over value ------------------------------- */

/* The rows layout's two columns carry a mark and its copy. This one carries a
   label and its value, and it is the same panel otherwise: the same surface, the
   same hairline above every row, the same padding and the same trims. The two
   differ only in what the first column holds, which is why they share the
   surface mixins above and the row tokens below.

   There is no mark column here, and that is the point of the layout. A row whose
   first cell is empty still reserves `--panel-mark`, so a labelled list poured
   into the rows layout would carry 2.4rem of dead space and a 0.95rem gap on
   every line, and its labels would sit in a mark's box rather than in a label's. */
.panel-list--pairs {
  /* The label column. It is a width of its own rather than the mark's, because a
     word is wider than a tile: nine rem holds the longest label this layout is
     built for and leaves the value the rest of the row. */
  --panel-label: 9rem;
  --panel-rule: #{$tint-primary};

  &.panel-list--dark {
    --panel-rule: #{$on-dark-line};
  }

  &:not(.panel-list--bare) {
    padding: 1.6rem 1.5rem;

    @media (min-width: 992px) {
      padding: 1.9rem 1.8rem;
    }
  }

  &.panel-list--light {
    @include panel-surface-light;
  }

  &.panel-list--dark {
    @include panel-surface-dark;
  }

  &:not(.panel-list--still) {
    /* The row answers its own pointer, which is the one thing this layout never
       got. A hovered row has lifted and glowed its mark since the rows layout
       was written, and the cards layout does the same, but the pairs layout was
       left without it: a pair was the one list on the site where pointing at a
       row produced nothing at all.

       That went unnoticed because a pair is usually a boxed panel, so the
       pointer still got an answer from the panel's own response above. It shows
       on the one caller that is `bare`, which is the case page's brief: the
       slab excludes the panel-level response at the source, because a list on a
       slab must not lift off it, so the list had no response of any kind and
       neither did any row inside it. The slab itself was the only thing on that
       page that answered a pointer.

       It is the same mixin the other two layouts call, so a pair and a row
       answer identically rather than nearly; and it is the row that answers,
       not the panel, because the hairlines between the rows are what make the
       list a list (see the note on the row fill above). */
    .panel-list-item {
      &:hover,
      &:focus-within {
        @include panel-lift;
      }
    }

    &.panel-list--light:not(.panel-list--bare) {
      @include panel-response($tint-primary-strong, $shadow-lift);
    }

    &.panel-list--dark:not(.panel-list--bare) {
      @include panel-response($on-dark-line-strong, $shadow-dark);
    }
  }

  /* The same cancel the rows layout takes, from the same mixin, for the same
     reason: a pair inside a slab is a ledger on the slab, not a card on it. */
  &.panel-list--bare {
    @include panel-bare;
  }

  .panel-list-item {
    align-items: baseline;
    border-top: 1px solid var(--panel-rule);
    /* The row gap is for the stacked pair below the breakpoint, where the label
       sits over its value and the two want a hair of air. The column gap is what
       separates a label from a value that share a line. */
    gap: 0.15rem 1.5rem;
    grid-template-columns: minmax(0, 1fr);
    padding: $panel-row-pad 0;

    &:first-child {
      border-top: 0;
      padding-top: $panel-row-trim;
    }

    &:last-child {
      padding-bottom: 0;
    }

    /* Two columns only once the row can hold both without crowding the value. A
       pair is read label first and value second, so the stacked form is the same
       order and the layout is not saying anything different below this width. */
    @media (min-width: 576px) {
      grid-template-columns: var(--panel-label) minmax(0, 1fr);
    }
  }

  /* A pair whose subject has a mark takes a third column for it, and only that
     pair does. `:has` asks the row what it is holding rather than the panel being
     told a second time, so a panel of plain pairs and a panel of marked ones need
     no prop to tell them apart, and no row reserves a column for a mark that is
     not coming.

     The label column gives 2rem back to the value when the mark arrives, because
     three columns now share the width the two had. The tile takes 2.4rem and its
     gap another 0.95, so a 9rem label column would leave the value about 180px in
     the narrower half of the page, which wraps two of these five values onto a
     second line. At 7rem the label column still holds the longest label the
     layout is built for ("Location" set at the $label-* step is about 78px) and
     the value keeps the room it needs. */
  .panel-list-item:has(.panel-list-mark) {
    gap: 0.15rem 0.95rem;
    grid-template-columns: var(--panel-mark) var(--panel-label) minmax(0, 1fr);

    --panel-label: 7rem;

    /* Below the two-column breakpoint the row cannot hold a mark, a label and a
       value on one line, so the mark and the label share the first line and the
       value takes the width under them, which is the same stacked reading the
       plain pair falls back to. The three-column template above applies at every
       width without this: `:has()` outranks the plain rule, so the `@media` on
       that rule never got a say and the 7rem label column stayed fixed on a
       phone. */
    @media (max-width: 575.98px) {
      grid-template-columns: var(--panel-mark) minmax(0, 1fr);
      grid-template-rows: auto auto;

      .panel-list-mark {
        grid-row: 1 / span 2;
      }

      .panel-list-label,
      .panel-list-value {
        grid-column: 2;
      }
    }
  }

  /* The same arrangement the rows layout offers, for a set of pairs short enough
     to read as two columns. */
  &.panel-list--cols-2 .panel-list-items {
    @media (min-width: 992px) {
      column-gap: 3.5rem;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
}

/* Cards: one surface per item -------------------------------------------- */
.panel-list--cards {
  .panel-list-items {
    display: grid;
    gap: 1.1rem;
    grid-template-columns: minmax(0, 1fr);
  }

  .panel-list-item {
    padding: 1.6rem 1.5rem;

    @media (min-width: 992px) {
      padding: 1.9rem 1.8rem;
    }
  }

  .panel-list-head {
    font-size: 1.15rem;
    line-height: 1.3;
    margin-bottom: 0.4rem;
  }

  /* The mark above the copy, so the card reads as a stacked tile and text
     rather than a two-column row. */
  &.panel-list--stacked .panel-list-item {
    display: block;
  }

  &.panel-list--light .panel-list-item {
    @include panel-box($ground-accent, $tint-primary);
    @include panel-beam($primary);
  }

  &.panel-list--dark .panel-list-item {
    @include panel-box($on-dark-fill, $on-dark-line);
    @include panel-beam($dark-accent);

    .panel-list-head {
      color: $on-dark-strong;
    }

    .panel-list-body {
      color: $on-dark-body;
    }
  }

  &:not(.panel-list--still) {
    .panel-list-item {
      &:hover,
      &:focus-within {
        @include panel-lift;
      }
    }

    &.panel-list--light .panel-list-item {
      @include panel-response($tint-primary-strong, $shadow-lift);

      &:hover,
      &:focus-within {
        .panel-list-head {
          color: $primary;
        }
      }
    }

    &.panel-list--dark .panel-list-item {
      @include panel-response($on-dark-line-strong, $shadow-dark);

      &:hover,
      &:focus-within {
        .panel-list-head {
          color: $dark-accent;
        }
      }
    }
  }

  &.panel-list--cols-2 .panel-list-items {
    @media (min-width: 768px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  &.panel-list--cols-3 .panel-list-items {
    @media (min-width: 768px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
}
</style>

<script>
import IconTile from '@/Components/IconTile.vue';

export default {
  components: {
    IconTile
  },

  props: {
    /* The panel's own kicker, e.g. "Included in the one-off price." It is a
       label for the list, not a document heading, so it renders as a p. */
    title: {
      type: String,
      default: ''
    },
    /* [{ icon, title, body }] for rows and cards, [{ label, value }] for pairs.
       A pair's `value` may be omitted when the caller supplies it through the
       `value` slot instead. */
    items: {
      type: Array,
      required: true
    },
    /* The mark for every row, used when the rows themselves share one. The promo
       checklists are six identical ticks, and naming that once is clearer than
       repeating `icon: 'check'` six times in the page data. */
    icon: {
      type: [String, Array, Object],
      default: null
    },
    /* light | dark */
    tone: {
      type: String,
      default: 'light'
    },
    /* rows | pairs | cards */
    layout: {
      type: String,
      default: 'rows'
    },
    /* icon | number */
    mark: {
      type: String,
      default: 'icon'
    },
    /* solid | outline */
    markVariant: {
      type: String,
      default: 'solid'
    },
    /* md (2.4rem) | sm (2.1rem) */
    markSize: {
      type: String,
      default: 'md'
    },
    /* cards only: the mark above the copy instead of beside it */
    stacked: {
      type: Boolean,
      default: false
    },
    /* Columns at the layout's own breakpoint: 768px for cards, 992px for rows.
       0 means the layout's natural count, which is 2 for cards and 1 for rows.
       It is a number rather than 0-or-2 because three cards in a row are as
       common here as two. */
    columns: {
      type: Number,
      default: 0
    },
    /* Rows end with a block from the extra slot. */
    trailing: {
      type: Boolean,
      default: false
    },
    /* The surrounding slab already provides the surface, so the list only
       divides it. Rows layout only: that is where the surface, the padding and
       the trims that `bare` drops are declared. */
    bare: {
      type: Boolean,
      default: false
    },
    /* The list is a document, not a set of choices, so nothing answers the
       pointer. The brand manifest needs this: a ruled spec sheet that
       highlights a row reads as a selectable menu. */
    still: {
      type: Boolean,
      default: false
    },
    /* The element the panel renders as. An aside panel stays an aside, so the
       landmark the page used to carry is not lost. */
    as: {
      type: String,
      default: 'div'
    }
  },

  computed: {
    cols() {
      if (this.columns > 0) {
        return this.columns;
      }

      return this.layout === 'cards' ? 2 : 1;
    }
  }
};
</script>
